/**
 * GH-945: the sentence a donor reads when Stripe.js reports a failed card payment.
 *
 * Stripe's `error.message` is written for developers as often as for payers ("Your card was
 * declined." for everything, or API wording on a network fault), so card errors go through the
 * shared classifier and its one table of donor sentences. The Payment Element's own inline field
 * problems (`validation_error`) stay as Stripe words them, because the Element already shows them
 * beside the field. Raw codes are never shown.
 */
import {
  CARD_FAILURE_REASON_BY_CODE,
  classifyCardFailure,
  cardFailureMessage,
} from "@givehere/contracts";

/** The parts of a Stripe.js `StripeError` this reads. */
export interface StripeErrorLike {
  readonly type?: string;
  readonly code?: string;
  readonly decline_code?: string;
  readonly message?: string;
  readonly payment_intent?: {
    readonly last_payment_error?: {
      readonly code?: string;
      readonly decline_code?: string;
    } | null;
  } | null;
}

export const donorMessageForStripeError = (error: StripeErrorLike): string => {
  if (error.type === "validation_error" && error.message) return error.message;

  const last = error.payment_intent?.last_payment_error;
  const code = error.code ?? last?.code;
  const declineCode = error.decline_code ?? last?.decline_code;
  const isCardProblem =
    error.type === "card_error" ||
    // An API refusal is a card problem only when it names one the table knows; anything else
    // (an internal fault) must not read as "declined".
    (error.type === "invalid_request_error" &&
      [code, declineCode].some((c) => c !== undefined && c in CARD_FAILURE_REASON_BY_CODE));
  if (isCardProblem && (code || declineCode)) {
    return cardFailureMessage(
      classifyCardFailure({ code: code ?? null, declineCode: declineCode ?? null }),
      "donor",
    );
  }
  if (error.type === "card_error") return cardFailureMessage("generic_decline", "donor");
  return cardFailureMessage("try_again", "donor");
};
